import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  SxProps,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/system';
import { debounce } from 'lodash';
import { ReactNode, useCallback } from 'react';
import { Search } from 'react-feather';

// Components
import IconFeather from 'src/components/common/Icon';
import PaperSelect from 'src/components/common/PaperSelect';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';

interface IProps {
  id: string;
  defaultKeyword?: string;
  required?: boolean;
  tooltipText?: string;
  label?: ReactNode;
  disabled?: boolean;
  isError?: boolean;
  value: any;
  inputValue: string;
  onChange: any;
  onTextInputChange?: any;
  options: any[];
  loading?: boolean;
  onBlur?: any;
  sx?: SxProps<Theme>;
  inputProps?: TextFieldProps;
}

const FormSearchHsCode = ({
  id,
  tooltipText = '',
  label,
  required = false,
  disabled = false,
  isError = false,
  value,
  onChange,
  onTextInputChange,
  options,
  loading,
  inputValue,
  onBlur,
  sx,
  inputProps,
}: IProps) => {
  const theme = useTheme();
  const handleTextInputChange = useCallback(
    debounce((e) => {
      onTextInputChange(e.target.value);
    }, 500),
    [],
  );

  const getDescription = (option: ExpectedAny) => {
    if (option.english_description) return option.english_description;
    else if (option.description) return option.description;
    else return '';
  };

  return (
    <Autocomplete
      sx={sx}
      value={value}
      onChange={(_, value) => onChange(value)}
      loading={loading}
      onBlur={onBlur}
      id={id}
      fullWidth
      size="small"
      freeSolo
      autoComplete
      options={options}
      disabled={disabled}
      popupIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Search />} />}
      PaperComponent={PaperSelect}
      autoHighlight
      getOptionLabel={(option) =>
        option ? `${option.code}${getDescription(option) ? `- ${getDescription(option)}` : ''}` : ''
      }
      renderOption={(props, option) => (
        <Tooltip title={getDescription(option)}>
          <Box gap={1} display={'flex'} component="li" alignItems="flex-start" {...props}>
            <Chip
              label={option.code}
              color="success"
              variant="filled"
              sx={{
                '&:hover': {
                  color: theme.palette.success.main,
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                },
              }}
            />
            <Typography
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                lineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {option ? getDescription(option) : ''}
            </Typography>
          </Box>
        </Tooltip>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...inputProps}
          id={`${id}-text`}
          error={isError}
          required={required}
          value={inputValue}
          InputLabelProps={{
            ...params.InputLabelProps,
          }}
          placeholder="Typing..."
          onChange={handleTextInputChange}
          label={label ? label : <span>HS Code {tooltipText && <TooltipFormLabel title={tooltipText} />}</span>}
        />
      )}
    />
  );
};

export default FormSearchHsCode;
